import { defineStore } from "pinia"
import { i18n } from "src/boot/i18n"
import { updatePriorityList } from "src/util/functions/lists"
import { SearchDomainType } from "src/util/types/dataModels/search"

export interface SearchStoreType {
  term: string | null | undefined
  domain: SearchDomainType | undefined
  enabled: boolean
  recentSearches: Record<SearchDomainType, Array<string>> | Record<string, never>
}

export const useSearchStore = defineStore("pinia-searchStore", {
  persist: true,

  state: (): SearchStoreType => ({
    term: null,
    domain: undefined,
    enabled: false,
    recentSearches: {},
  }),

  actions: {
    /**
     * Sets everything but the recentSearches data back to default values.
     */
    resetIntermediateData() {
      this.term = null
      this.domain = undefined
      this.enabled = false
    },

    resetAllRecentSearches() {
      this.recentSearches = {}
    },

    /**
     * Recent a specific recent store based on domain provided.
     * If no domain is given, it will reset the current one.
     *
     * @param searchDomain
     */
    resetSpecificRecentSearch(searchDomain?: SearchDomainType) {
      this.getRecentSearch(searchDomain).length = 0
    },

    /**
     * Returns the appropriate recent search list based on provided or currently selected search store domain.
     * Initialises the list if it is not.
     * If there is no valid domain, then returns an empty array.
     *
     * @param searchDomain
     */
    getRecentSearch(searchDomain?: SearchDomainType) {
      let result: string[]

      if (searchDomain) {
        result = this.recentSearches[searchDomain]
      } else if (this.domain) {
        result = this.recentSearches[this.domain]
      } else {
        result = []
      }

      // If the list is not initialised, then initialise it
      if (!result) {
        result = []
      }
      return result
    },

    /**
     * Adds an item to a recent searches list based on provided or currently active search domain.
     *
     * @param item
     * @param searchDomain
     * @param maxItemsInList
     * @throws {Error} - Unable to determine active domain
     */
    addItemToRecentSearches(item: string, searchDomain?: SearchDomainType, maxItemsInList = 5) {
      if (!searchDomain) {
        if (!this.domain) throw new Error("Unable to determine active domain")

        searchDomain = this.domain
      }

      const listToUpdate = this.getRecentSearch(searchDomain)
      this.recentSearches[searchDomain] = updatePriorityList(item, listToUpdate, maxItemsInList)
    },

    /**
     * Removes an item from a recent searches list based on specified or currently active search domain.
     *
     * @param itemIndex
     * @param searchDomain
     * @throws {Error} Bad index.
     */
    removeItemFromRecentSearches(itemIndex: number, searchDomain?: SearchDomainType) {
      if (itemIndex < 0) {
        throw new Error("Bad index provided")
      }

      this.getRecentSearch(searchDomain).splice(itemIndex, 1)
    },

    getPlaceholderEntity() {
      const { t } = i18n.global

      switch (this.domain) {
        case "items": {
          return t("item.title")
        }
        case "issues": {
          return t("issue.title")
        }
        case "models": {
          return t("model.title")
        }
        case "performedWork": {
          return t("performedWork.title_performed_work")
        }
        case "areas": {
          return t("area.title")
        }
        case "layouts": {
          return t("layout.title")
        }
        case "workspaces": {
          return t("workspace.title")
        }
        case "zones": {
          return t("zone.title")
        }
        case "workOrders": {
          return t("workOrder.title")
        }
        case "unsubmittedWork": {
          return t("toDo.unsubmitted_work")
        }
      }
    },

    getManagerPlaceholderEntity() {
      const { t } = i18n.global

      switch (this.domain) {
        case "items": {
          return t("items.title")
        }
        case "issues": {
          return t("issues.title")
        }
        case "models": {
          return t("models.title")
        }
        case "performedWork": {
          return t("performedWork.title")
        }
        case "areas": {
          return t("areas.title")
        }
        case "layouts": {
          return t("layouts.title")
        }
        case "workspaces": {
          return t("workspaces.title")
        }
        case "zones": {
          return t("zones.title")
        }
        case "workOrders": {
          return t("workOrder.title")
        }
        case "unsubmittedWork": {
          return t("toDos.unsubmitted_work")
        }
      }
    },
  },
})
